import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { UserService } from "./user.service";
import { UserModel } from "../model/user.model";
import { AuthenticationService } from "./auth.service";
import { SharedDataService } from "./shared-data.service";
import { Observable, Subject } from "rxjs";
import { AttributeModel } from "../main/dynamic/model/attribute.model";
import { WorkflowGatewayService } from "./workflow-gateway.service";
import { PagingContainerModel } from "../model/paging-container.model";
import { ProcessSummaryModel } from "../model/process-summary.model";

@Injectable({
    providedIn: 'root'
})
export class BindService {

    private bindedSubject = new Subject<boolean>();
    private bindingRequest = new Subject<AttributeModel>();

    keyMap: string = "key"
    valueMap: string = "value"
    typeMap: string = "Option"

    constructor(
        private http: HttpClient,
        private userService: UserService,
        private authService: AuthenticationService,
        private sharedDataService: SharedDataService,
        private workflowService: WorkflowGatewayService) {
    }

    get headers() {
        return new HttpHeaders()
            .set("Content-Type", "application/json")
            .set("Authorization", `Bearer ${this.authService.getToken()}`)
    }

    getBindedSubject(): Observable<boolean> {
        return this.bindedSubject.asObservable();
    }

    getBindingRequestSubject(): Observable<AttributeModel> {
        return this.bindingRequest.asObservable();
    }

    async loadBindings(attribute: AttributeModel) {
        
        let binding = attribute.attributes
            .find(attr => attr.type.toLowerCase() == "binding")

        if(!binding) {
            return
        }

        await this.load(binding)
    }

    async load(binding: AttributeModel) {
        if(!binding.value) {
            return
        }

        if(this.valueEquals(binding, "Users")) {
            await this.loadUsers(binding)
        } else if(this.valueEquals(binding, "Units")) {
            this.loadUnits(binding)
        } else if(this.valueEquals(binding, "Policies")) {
            await this.loadPolicies(binding)
        } else if(binding.value.startsWith("http")) {
            this.setKeyValueMap(binding)
            await this.bindFromHttpRequest(binding);
        } else if(this.valueEquals(binding, "SharedData")) {
            this.bindFromSharedService(binding);
        } else if(this.valueEquals(binding, "Processes")) {
            this.setKeyValueMap(binding);
            const policyIdOpt = binding.options.find(opt => opt.key.toLowerCase() == "policyid");
            const statusOpt = binding.options.find(opt => opt.key.toLowerCase() == "status");

            if(policyIdOpt && policyIdOpt.value) {
                let searchParams = {
                    policyId: policyIdOpt.value,
                    status: statusOpt?.value
                };

                await this.workflowService
                    .getSummary(1, 20, {
                        searchParams: searchParams
                    })
                    .toPromise()
                    .then((response: PagingContainerModel) => {
                        if (response) {
                            binding.attributes = response.items.map(i => {
                                return {
                                    key: this.keyMap == "id" ? i.id : this.getBindingAttributeValue(i.attributes, this.keyMap),
                                    value: this.getBindingAttributeValue(i.attributes, this.valueMap),
                                    type: this.typeMap
                                };
                            });
                        }
                    });
            }
        }

        this.notifyBinded()
    }

    private getBindingAttributeValue(attributes: AttributeModel[], key: string): string {
        const attribute = attributes.find(attr => attr.key.toLowerCase() === key.toLowerCase());
        return attribute ? attribute.value : null;
    }

    private valueEquals(attribute: AttributeModel, value: string): boolean {
        return null != attribute.value && attribute.value.toLowerCase() == value.toLowerCase()
    }
    
    private setKeyValueMap(binding: AttributeModel) {
        let keyMapOpt = binding.options.find(opt => opt.key == "bindValue")
        let valueMapOpt = binding.options.find(opt => opt.key == "bindLabel")
        let typeMapOpt = binding.options.find(opt => opt.key == "bindType")
    
        if(keyMapOpt) {
            this.keyMap = keyMapOpt.value
        }
    
        if(valueMapOpt) {
            this.valueMap = valueMapOpt.value
        }

        if(typeMapOpt) {
            this.typeMap = typeMapOpt.value
        } else {
            this.typeMap = "Option"
        }
    }

    private async loadPolicies(binding: AttributeModel) {
        await this.wait(1000);

        let policies = <any[]>JSON.parse(sessionStorage.getItem("policies-names"))

        if(policies) {
            binding.attributes = policies
                .map(policy => {
                    return {
                        key: policy.key,
                        value: policy.value,
                        type: "option"
                    };
                })
                .sort((a, b) =>
                    this.getUserFullName(a) < this.getUserFullName(b) ? -1 : 1
                );
        }
    }

    private loadUsers(binding: AttributeModel): Promise<any> {
        return this.userService
            .getAllUsers()
            .toPromise()
            .then((outerUsers: UserModel[]) => {
                binding.attributes = outerUsers
                    .map(user => {
                        return {
                            key: user.id.toLowerCase(),
                            value: user.name,
                            type: "option"
                        };
                    })
                    .sort((a, b) =>
                        this.getUserFullName(a) < this.getUserFullName(b) ? -1 : 1
                    );
            });
    }
    
    private getUserFullName(user: AttributeModel): string {
        if (user) {
            return user.value ? user.value : user.key;
        }
    }

    private loadUnits(binding: AttributeModel) {
        var allowedUnits = this.userService.getCurrentUserAttributes("Unidades")  
                                .sort((a,b)=> a.value.localeCompare(b.value));;

        binding.attributes = allowedUnits;
    }

    private async bindFromHttpRequest(binding: AttributeModel) {
        await this.http.get(binding.value, {
            headers: this.headers
        })
        .toPromise()
        .then((response: any[]) => {
            if (response) {
                binding.attributes = response.map(i => {
                    return {
                        key: i[this.keyMap],
                        value: i[this.valueMap],
                        type: i[this.typeMap]
                    };
                });
            }
        });
    }

    private bindFromSharedService(binding: AttributeModel) {
        let path = binding.attributes
            .find(attr => attr.key.toLowerCase() == "bindingpath" && attr.value);

        if (path) {
            let categoryOpt = path.options
                .find(opt => opt.key.toLowerCase() == "category");

            let items = this.sharedDataService
                .getData(path.value, categoryOpt ? categoryOpt.value : null);

            binding.attributes = [path];
            binding.attributes = binding.attributes.concat(items.map(i => {
                return {
                    uid: i.uid,
                    key: i.key,
                    value: i.value ? i.value : i.key,
                    type: "option",
                    attributes: [],
                    options: []
                };
            }));
        }
    }

    notifyBinded() {
        this.bindedSubject.next(true);
    }

    requestBinding(sourceAttr: AttributeModel) {
        if(sourceAttr && sourceAttr.key && sourceAttr.value) {
            this.bindingRequest.next(sourceAttr);
        }
    }

    private async wait(ms) {
        return new Promise(resolve => {
            setTimeout(resolve, ms);
        });
    }
}